import React, { useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import { apiRequest, getEntityId, unwrapCollection } from '../../api/client';
import SafeIcon from '../../common/SafeIcon';
import Flashcard from '../../components/study/Flashcard';
import StudyFilters from '../../components/study/StudyFilters';
import { useAuth } from '../../context/AuthContext';
import {
  getDifficulty,
  getReviewUpdate,
  isDue,
  shuffleArray
} from '../../utils/cards';

const { FiCheckCircle, FiRefreshCw, FiShuffle } = FiIcons;

function getCardId(card) {
  return String(getEntityId(card) || card?.front_text || 'unknown-card');
}

function getProgressCardId(progress) {
  return (
    getEntityId(progress?.flashcard_id) ||
    progress?.card_id ||
    progress?.flashcard_id
  );
}

function StudyPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ cards: [], progress: [] });
  const [filters, setFilters] = useState({ difficulty: '', variant: '' });
  const [randomMode, setRandomMode] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);
  const [state, setState] = useState({
    loading: true,
    saving: false,
    error: ''
  });

  const load = async () => {
    setState((current) => ({ ...current, loading: true, error: '' }));

    try {
      const [cardPayload, progressPayload] = await Promise.all([
        apiRequest('/flashcards', { auth: false }),
        apiRequest('/progress')
      ]);

      setData({
        cards: unwrapCollection(cardPayload, ['flashcards', 'items', 'results']),
        progress: unwrapCollection(progressPayload, [
          'progress',
          'items',
          'results'
        ])
      });
      setCardIndex(0);
      setState({ loading: false, saving: false, error: '' });
    } catch (error) {
      console.error('Study deck load failed:', error);
      setState({
        loading: false,
        saving: false,
        error: error.message || 'Unable to load study cards.'
      });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const progressByCard = useMemo(() => {
    const map = new Map();

    data.progress.forEach((item) => {
      const id = getProgressCardId(item);

      if (id) {
        map.set(String(id), item);
      }
    });

    return map;
  }, [data.progress]);

  const deckCards = useMemo(() => {
    const cards = data.cards.filter((card) => (
      (!filters.difficulty ||
        getDifficulty(card.target_band) === filters.difficulty) &&
      (!filters.variant || card.english_variant === filters.variant)
    ));

    return randomMode ? shuffleArray(cards) : cards;
  }, [data.cards, filters, randomMode]);

  const dueCount = useMemo(() => (
    deckCards.filter((card) => (
      isDue(progressByCard.get(getCardId(card)))
    )).length
  ), [deckCards, progressByCard]);

  const knownCount = useMemo(() => (
    new Set(
      data.progress
        .filter((item) => item.status === 'known')
        .map(getProgressCardId)
        .filter(Boolean)
        .map(String)
    ).size
  ), [data.progress]);

  const completionPercent = data.cards.length
    ? Math.round((knownCount / data.cards.length) * 100)
    : 0;

  useEffect(() => {
    setCardIndex((index) => Math.min(
      index,
      Math.max(0, deckCards.length - 1)
    ));
  }, [deckCards.length]);

  const activeCard = deckCards[cardIndex];

  const review = async (known) => {
    if (!activeCard || state.saving) {
      return;
    }

    const cardId = getCardId(activeCard);
    const current = progressByCard.get(cardId);
    const update = getReviewUpdate(current, known);

    setState((value) => ({ ...value, saving: true, error: '' }));

    try {
      const saved = current
        ? await apiRequest(`/progress/${getEntityId(current)}`, {
            method: 'PATCH',
            body: JSON.stringify(update)
          })
        : await apiRequest('/progress', {
            method: 'POST',
            body: JSON.stringify({
              ...update,
              user_id: getEntityId(user),
              flashcard_id: getEntityId(activeCard)
            })
          });

      const nextProgress = saved || {
        ...update,
        ...(current || {}),
        flashcard_id: getEntityId(activeCard)
      };

      setData((value) => ({
        ...value,
        progress: current
          ? value.progress.map((item) => (
              getEntityId(item) === getEntityId(current)
                ? nextProgress
                : item
            ))
          : [...value.progress, nextProgress]
      }));

      setState({ loading: false, saving: false, error: '' });

      if (cardIndex < deckCards.length - 1) {
        setCardIndex((index) => index + 1);
      }
    } catch (error) {
      console.error('Progress save failed:', error);
      setState((value) => ({
        ...value,
        saving: false,
        error: error.message || 'Unable to save progress.'
      }));
    }
  };

  const skip = () => {
    if (state.saving || !activeCard) {
      return;
    }

    if (cardIndex < deckCards.length - 1) {
      setCardIndex((index) => index + 1);
    }
  };

  const goNext = () => {
    if (cardIndex < deckCards.length - 1) {
      setCardIndex((index) => index + 1);
    }
  };

  const goBack = () => {
    if (cardIndex > 0) {
      setCardIndex((index) => index - 1);
    }
  };

  const updateFilters = (value) => {
    setFilters(value);
    setCardIndex(0);
  };

  const toggleRandom = () => {
    setRandomMode((value) => !value);
    setCardIndex(0);
  };

  return (
    <section>
      <header className="page-heading">
        <div>
          <span className="eyebrow">Today’s session</span>
          <h1>Ready to review?</h1>
          <p>Work through your vocabulary one card at a time.</p>
        </div>

        <div className="header-actions">
          <div className="due-count">
            <b>{knownCount}</b>
            <span>known · {completionPercent}% complete</span>
          </div>

          <button
            type="button"
            className={`button ${randomMode ? 'primary' : 'secondary'}`}
            onClick={toggleRandom}
            aria-pressed={randomMode}
          >
            <SafeIcon icon={FiShuffle} />
            {randomMode ? 'Random revision: on' : 'Random revision'}
          </button>

          <div className="due-count">
            <b>{dueCount}</b>
            <span>cards due</span>
          </div>
        </div>
      </header>

      <StudyFilters filters={filters} setFilters={updateFilters} />

      {state.error && <div className="error-banner">{state.error}</div>}

      {state.loading ? (
        <div className="center-state">
          <span className="loader" />
          Preparing your deck…
        </div>
      ) : activeCard ? (
        <Flashcard
          key={getCardId(activeCard)}
          card={activeCard}
          onReview={review}
          onSkip={skip}
          onNext={goNext}
          onBack={goBack}
          canGoBack={cardIndex > 0}
          canGoNext={cardIndex < deckCards.length - 1}
          saving={state.saving}
        />
      ) : (
        <div className="empty-state">
          <span>
            <SafeIcon icon={FiCheckCircle} />
          </span>
          <h2>You’re all caught up</h2>
          <p>No cards are available with the current filters.</p>
          <button type="button" className="button secondary" onClick={load}>
            <SafeIcon icon={FiRefreshCw} />
            Refresh deck
          </button>
        </div>
      )}
    </section>
  );
}

export default StudyPage;